/**
 * `renderText` for the library's text part (APP-3405). @mui/x-chat's default renderer applies non-CommonMark
 * intraword `_` emphasis, so a plain (non-tool) reply italicized `portfolio_long_only` while every Kelly-owned
 * <ChatMarkdown> call site rendered it literally. Its `renderText` is module-private, so parity is rebuilt from
 * the library's public pieces (headless fence repair, themed ChatCodeBlock, drill-in link tabIndex) — the one
 * behaviour not reachable that way is its lazy `remend` upgrade, so mid-stream repair is fence completion only.
 */

import { Fragment, useMemo } from "react"
import type { ComponentProps } from "react"
import Markdown from "markdown-to-jsx"
import type { MarkdownToJSX } from "markdown-to-jsx"
import { useMessageContentTabIndex } from "@mui/x-chat-headless"
import { normalizeMarkdownForRender } from "@mui/x-chat-headless/internals"
import { MARKDOWN_ELEMENT_OVERRIDES, MARKDOWN_ELEMENT_RULE, escapeIntrawordUnderscores } from "./chat-markdown"
import { MarkdownErrorBoundary } from "./markdown-error-boundary"

const SAFE_URL_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"]

// Applied to every link href / image src (mirrors the library's markdown sanitizer): an unparseable value is a
// relative URL and passes, but protocol-relative `//host` resolves to another origin with no scheme, so it drops.
function sanitizeUrl(value: string): string | null {
  try {
    return SAFE_URL_PROTOCOLS.includes(new URL(value).protocol) ? value : null
  } catch {
    return !value.includes(":") && !/^[/\\]{2}/.test(value) ? value : null
  }
}

// Interior links join the message list's drill-in model — out of the tab order until the user drills into the
// message with Enter — so the whole list stays one Tab stop. Mouse clicks work throughout.
function MarkdownLink({ children, ...props }: ComponentProps<"a">) {
  const tabIndex = useMessageContentTabIndex()
  return <a target="_blank" rel="noopener noreferrer" tabIndex={tabIndex} {...props}>{children}</a>
}

const MARKDOWN_OPTIONS: MarkdownToJSX.Options = {
  // Block-level wrappers (so a lone line is a <p>, matching the bubble's `& p` styling) via a Fragment, so no
  // extra DOM node wraps multi-block content.
  forceBlock: true,
  wrapper: Fragment,
  // Untrusted model output: never transcribe raw HTML into JSX.
  disableParsingRawHTML: true,
  sanitizer: sanitizeUrl,
  overrides: { ...MARKDOWN_ELEMENT_OVERRIDES, a: { component: MarkdownLink } },
  renderRule: MARKDOWN_ELEMENT_RULE,
}

// Escape before the fence repair: the escape's exemptions reason about the raw stream shape (an unterminated
// fence still arriving), and it only inserts backslashes, so fence parity is untouched either way.
// Its own component so the transforms run below the boundary, not while ChatTextPart builds this element's props.
function RepairedMarkdown({ text }: { text: string }) {
  const source = useMemo(() => normalizeMarkdownForRender(escapeIntrawordUnderscores(text)), [text])
  return <Markdown options={MARKDOWN_OPTIONS}>{source}</Markdown>
}

function ChatTextPart({ text }: { text: string }) {
  return (
    <MarkdownErrorBoundary source={text}>
      <RepairedMarkdown text={text} />
    </MarkdownErrorBoundary>
  )
}

/** Stable module-level identity — the headless TextPart memoizes the renderer, so an inline arrow would churn it. */
export const renderChatText = (text: string) => <ChatTextPart text={text} />
